import React from 'react'
import format from 'date-fns/format'
import { MaybeLoaded } from 'json-react-layouts-data-loader'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'

import {
    StyledHeader,
    StyledHeadline,
} from 'component-library/cards/LandscapeWide/Styled/StyledHeader.styled'
import {
    StyledWrapper,
    StyledSponsoredLabel,
    StyledTeaser,
    StyledText,
    StyledSponsoredByline,
} from 'component-library/cards/LandscapeWide/Styled/StyledText.styled'
import {
    StyledTimestampBlock,
    StyledTime,
} from 'component-library/cards/LandscapeWide/Styled/StyledMedia.styled'
import { StyledMedia } from 'component-library/cards/LandscapeWide/Styled/StyledMedia.styled'

import {
    StyledLandscapeWide,
    StyledSharing,
} from 'component-library/cards/LandscapeWide/LandscapeWide.styled'

import { toLinkWithHint } from 'common/routing/route-hints'
import { Product } from 'component-library/__product'
import { StyledHeadlineSkeleton } from '../LandscapeStacked/LandscapeStacked.styled'
import { FixedRatio, CardBreakpointRatios } from 'common/client-data-types/image-item'
import { MediaMode } from '../CardMedia/CardMedia'
import { useProduct } from 'common/hooks/useProduct'
import {
    PublicationCardItem,
    isPublicationCardItemWithVideo,
} from 'common/client-data-types/card-types'
import { createCardClickedEvent } from 'common/events/collection-events'
import { TimestampType } from 'component-library/content/CardTimestamp/CardTimestamp'

export interface Props extends CommonCardProps {
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    mediaMode?: MediaMode
    hasBackground?: boolean
    timestamp?: TimestampType

    cardNumber: number

    item: MaybeLoaded<PublicationCardItem>
}

export const LandscapeWide: React.FC<Props> = props => {
    const product = useProduct()

    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!props.item.loaded) {
            e.preventDefault()
            return
        }

        props.onEvent(
            createCardClickedEvent(
                props.item.result,
                'InternalLandscapeWide',
                'LandscapeWide',
                props.cardNumber
            )
        )
    }

    const getImageWidth = (product: Product) => {
        switch (product) {
            case Product.PerthNow:
                return 88
            case Product.TheWest:
                return 110
            case Product.SevenNews:
                return 126
        }
    }

    const renderImageOrTimestamp = () => {
        // Never show timestamps when the item is sponsored, but it has preference over showing the image
        if (
            props.timestamp === 'list-style' &&
            props.item.loaded &&
            !props.item.result.isSponsored &&
            props.item.result.publicationDate
        ) {
            const humanReadableTimestamp = format(props.item.result.publicationDate, 'h:mmA')
            return (
                <StyledTimestampBlock>
                    <StyledTime>{humanReadableTimestamp}</StyledTime>
                </StyledTimestampBlock>
            )
        }

        return (
            <StyledMedia
                item={props.item}
                fixedRatio={props.fixedRatio || '4:3'}
                imageWidths={getImageWidth(product)}
                onEvent={props.onEvent}
                disableImageLazyLoad={props.disableImageLazyLoad}
                hideMediaIcon={true}
                mediaMode={props.mediaMode}
            />
        )
    }

    const renderCardText = () => {
        if (!props.item.loaded) {
            return <StyledTeaser teaserOrSkeletonLines={3} hasBackground={props.hasBackground} />
        }

        if (props.item.result.isSponsored) {
            return (
                <StyledText>
                    <StyledTeaser
                        teaserOrSkeletonLines={props.item.result.teaser}
                        hasBackground={props.hasBackground}
                    />
                    <StyledSponsoredLabel />
                    <StyledSponsoredByline>{props.item.result.byline}</StyledSponsoredByline>
                </StyledText>
            )
        }

        return (
            <StyledTeaser
                teaserOrSkeletonLines={props.item.result.teaser}
                hasBackground={props.hasBackground}
            />
        )
    }

    const { hideKicker = true, className, hasBackground } = props

    // Sponsored cards in perthnow do not display kickers
    const cardItem = props.item

    return (
        <StyledLandscapeWide
            className={className}
            data-topic={props.item.loaded && props.item.result.primaryTopic.id}
        >
            <StyledHeader
                to={toLinkWithHint(props.item)}
                onClick={linkClicked}
                isLoading={!props.item.loaded}
            >
                {cardItem.loaded ? (
                    <StyledHeadline
                        kickerText={cardItem.result.kicker}
                        headlineText={cardItem.result.shortHeadline}
                        hasBackground={hasBackground}
                        hideKicker={hideKicker}
                        fontScale={1.2}
                        isLive={cardItem.result.isLive}
                        showVideoIcon={isPublicationCardItemWithVideo(cardItem.result)}
                        isSponsored={cardItem.result.isSponsored}
                        publicationKind={cardItem.result.publicationKind}
                    />
                ) : (
                    <StyledHeadlineSkeleton hasBackground={hasBackground} fontScale={1.2} />
                )}
            </StyledHeader>
            <StyledWrapper
                to={toLinkWithHint(props.item)}
                onClick={linkClicked}
                isLoading={!props.item.loaded}
            >
                {renderImageOrTimestamp()}
                {renderCardText()}
            </StyledWrapper>

            <StyledSharing
                loading={!props.item.loaded}
                url={!props.item.loaded ? '' : props.item.result._self}
                text={!props.item.loaded ? '' : props.item.result.shortHeadline}
                onEvent={props.onEvent}
                isGhostStyle={true}
                shareOptions={['facebook', 'twitter', 'email', 'clipboard']}
            />
        </StyledLandscapeWide>
    )
}
LandscapeWide.displayName = 'LandscapeWide'
